import React, {
  FormEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import classNames from "classnames";
import {
  ArrowUp,
  History,
  LoaderCircle,
  Menu,
  MessageCircle,
  Plus,
  Sparkles,
} from "lucide-react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { useDispatch, useSelector } from "react-redux";
import {
  addOptimisticGlobalChatMessageAction,
  createLocalGlobalChatConversationAction,
  generateGlobalChatConversationTitleAction,
  listGlobalChatConversationsAction,
  listGlobalChatMessagesAction,
  persistGlobalChatConversationAction,
  sendGlobalChatMessageAction,
} from "../../state/globalChat/actions";
import {
  GlobalChatConversation,
  GlobalChatMessage,
} from "../../state/globalChat/types";
import { listMemoriesAction } from "../../state/memories/actions";
import { Memory } from "../../state/memories/types";
import { getRecommendedQuestionPrompts } from "../../state/memories/utils";
import { State } from "../../state/state";
import {
  getChatConversationPath,
  getMemoryDetailPath,
} from "../../Router/routes";
import styles from "./ChatPage.stylesheet.module.scss";

const formatConversationTime = (dateValue: string) => {
  const date = new Date(dateValue);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (isSameDay(date, today)) {
    return date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  }
  if (isSameDay(date, yesterday)) return "Yesterday";
  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
  });
};

const formatConversationGroup = (dateValue: string) => {
  const date = new Date(dateValue);
  const today = new Date();
  const startOfDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const dayDifference = Math.round(
    (startOfToday.getTime() - startOfDate.getTime()) / (24 * 60 * 60 * 1000),
  );

  if (dayDifference === 0) return "Today";
  if (dayDifference === 1) return "Yesterday";
  if (dayDifference < 7) return "This week";
  return date.toLocaleDateString([], {
    month: "long",
    year: "numeric",
  });
};

const groupConversationIds = (
  conversationIds: string[],
  conversations: State["globalChat"]["conversations"],
) => {
  const groups: { label: string; ids: string[] }[] = [];

  for (const conversationId of conversationIds) {
    const conversation = conversations[conversationId];
    if (!conversation) continue;

    const label = formatConversationGroup(conversation.updated_at);
    const existingGroup = groups.find((group) => group.label === label);
    if (existingGroup) {
      existingGroup.ids.push(conversationId);
    } else {
      groups.push({ label, ids: [conversationId] });
    }
  }

  return groups;
};

interface ChatComposerProps {
  disabled: boolean;
  onSend: (text: string) => void;
  placeholder: string;
}

const ChatComposer = ({ disabled, onSend, placeholder }: ChatComposerProps) => {
  const [text, setText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const submit = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setText("");
  }, [disabled, onSend, text]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter") return;
    if (event.shiftKey) return;
    if (event.metaKey || event.ctrlKey) {
      event.preventDefault();
      const textarea = event.currentTarget;
      const currentText = textarea.value;
      const nextText = `${currentText.slice(
        0,
        textarea.selectionStart,
      )}\n${currentText.slice(textarea.selectionEnd)}`.slice(0, 3000);
      const cursorPosition = Math.min(
        textarea.selectionStart + 1,
        nextText.length,
      );
      setText(nextText);
      window.requestAnimationFrame(() => {
        textarea.selectionStart = cursorPosition;
        textarea.selectionEnd = cursorPosition;
      });
      return;
    }
    event.preventDefault();
    submit();
  };

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "0px";
    const nextHeight = Math.min(textarea.scrollHeight, 140);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY =
      textarea.scrollHeight > nextHeight ? "auto" : "hidden";
  }, [text]);

  return (
    <form className={styles.chatComposer} onSubmit={handleSubmit}>
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(event) => setText(event.target.value.slice(0, 3000))}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        rows={1}
      />
      <button
        type="submit"
        disabled={disabled || !text.trim()}
        aria-label="Send message"
      >
        <ArrowUp aria-hidden="true" size={19} strokeWidth={2.4} />
      </button>
    </form>
  );
};

const ThinkingIndicator = () => (
  <div className={styles.thinkingIndicator} aria-label="Replay is thinking">
    <span />
    <span />
    <span />
  </div>
);

interface ChatMessageBubbleProps {
  memories: { [memoryId: string]: Memory };
  message: GlobalChatMessage;
}

const ChatMessageBubble = ({ memories, message }: ChatMessageBubbleProps) => {
  const isUser = message.from === "user";
  const referencedMemories = message.referenced_memory_ids
    .map((memoryId) => memories[memoryId])
    .filter(Boolean);

  return (
    <article
      className={classNames(styles.messageRow, {
        [styles.messageRowUser]: isUser,
      })}
    >
      {!isUser && (
        <span className={styles.messageAvatar} aria-hidden="true">
          <Sparkles size={16} strokeWidth={2} />
        </span>
      )}
      <div
        className={classNames(styles.messageBubble, {
          [styles.messageBubbleUser]: isUser,
          [styles.messageBubbleAi]: !isUser,
        })}
      >
        <p>{message.message}</p>
        {!isUser && referencedMemories.length > 0 && (
          <div
            className={styles.messageSources}
            aria-label="Referenced memories"
          >
            {referencedMemories.map((memory) => (
              <Link
                key={memory.id}
                to={getMemoryDetailPath(memory.id)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {memory.name}
              </Link>
            ))}
          </div>
        )}
      </div>
    </article>
  );
};

interface ConversationRowProps {
  conversation: GlobalChatConversation;
  isActive: boolean;
  isLoading: boolean;
  onSelect?: () => void;
}

const ConversationRow = ({
  conversation,
  isActive,
  isLoading,
  onSelect,
}: ConversationRowProps) => (
  <div
    className={classNames(styles.conversationRow, {
      [styles.conversationRowActive]: isActive,
    })}
  >
    <Link to={getChatConversationPath(conversation.id)} onClick={onSelect}>
      <span
        className={classNames(styles.conversationTitle, {
          [styles.conversationTitlePending]: conversation.title === "New chat",
        })}
      >
        {conversation.title === "New chat" ? (
          <LoaderCircle
            className={styles.titleSpinner}
            aria-label="Generating chat title"
            role="status"
            size={16}
            strokeWidth={2}
          />
        ) : (
          conversation.title
        )}
      </span>
      <span className={styles.conversationTime}>
        {formatConversationTime(conversation.updated_at)}
      </span>
    </Link>
    {isLoading && (
      <LoaderCircle
        className={styles.conversationLoadingSpinner}
        aria-label="Waiting for chat response"
        role="status"
        size={16}
        strokeWidth={2}
      />
    )}
  </div>
);

interface ChatSidebarProps {
  activeConversationId: string | undefined;
  conversationGroups: { label: string; ids: string[] }[];
  conversations: State["globalChat"]["conversations"];
  isLoading: boolean;
  isMobileOpen: boolean;
  loadingByConversationId: State["globalChat"]["loadingByConversationId"];
  onClose: () => void;
}

const ChatSidebar = ({
  activeConversationId,
  conversationGroups,
  conversations,
  isLoading,
  isMobileOpen,
  loadingByConversationId,
  onClose,
}: ChatSidebarProps) => (
  <aside
    className={classNames(styles.chatSidebar, {
      [styles.chatSidebarOpen]: isMobileOpen,
    })}
    aria-label="Chat history"
  >
    <div className={styles.sidebarTop}>
      <div>
        <span className={styles.sidebarEyebrow}>Replay</span>
        <h1>Chat</h1>
      </div>
      <Link
        className={styles.newChatButton}
        to={getChatConversationPath("new")}
        aria-label="Start a new chat"
      >
        <Plus aria-hidden="true" size={18} strokeWidth={2.1} />
      </Link>
    </div>

    <div className={styles.conversationList}>
      {isLoading ? (
        <div className={styles.conversationSkeleton} aria-label="Loading chats">
          <span />
          <span />
          <span />
        </div>
      ) : conversationGroups.length === 0 ? (
        <div className={styles.emptyHistory}>
          <History aria-hidden="true" size={18} strokeWidth={1.9} />
          <span>No chats yet</span>
        </div>
      ) : (
        conversationGroups.map((group) => (
          <section className={styles.conversationGroup} key={group.label}>
            <h2>{group.label}</h2>
            {group.ids.map((conversationId) => {
              const conversation = conversations[conversationId];
              if (!conversation) return null;
              return (
                <ConversationRow
                  key={conversation.id}
                  conversation={conversation}
                  isActive={activeConversationId === conversation.id}
                  isLoading={Boolean(loadingByConversationId[conversation.id])}
                  onSelect={onClose}
                />
              );
            })}
          </section>
        ))
      )}
    </div>
  </aside>
);

interface EmptyChatProps {
  disabled: boolean;
  onPrompt: (prompt: string) => void;
  prompts: string[];
}

const EmptyChat = ({ disabled, onPrompt, prompts }: EmptyChatProps) => (
  <div className={styles.emptyChat}>
    <span className={styles.emptyChatIcon} aria-hidden="true">
      <MessageCircle size={30} strokeWidth={1.8} />
    </span>
    <h2>Ask across your memories.</h2>
    <div className={styles.promptGrid}>
      {prompts.map((prompt) => (
        <button
          type="button"
          key={prompt}
          disabled={disabled}
          onClick={() => onPrompt(prompt)}
        >
          {prompt}
        </button>
      ))}
    </div>
  </div>
);

export const ChatPage = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<ThunkDispatch<State, unknown, AnyAction>>();
  const user = useSelector((state: State) => state.user.user);
  const memories = useSelector((state: State) => state.memories.items);
  const hasLoadedMemories = useSelector(
    (state: State) => state.memories.hasLoaded,
  );
  const conversations = useSelector(
    (state: State) => state.globalChat.conversations,
  );
  const conversationOrder = useSelector(
    (state: State) => state.globalChat.conversationOrder,
  );
  const hasLoadedConversations = useSelector(
    (state: State) => state.globalChat.hasLoadedConversations,
  );
  const messagesByConversationId = useSelector(
    (state: State) => state.globalChat.messagesByConversationId,
  );
  const loadingByConversationId = useSelector(
    (state: State) => state.globalChat.loadingByConversationId,
  );
  const [isLoadingConversations, setIsLoadingConversations] = useState(
    !hasLoadedConversations,
  );
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const messagesRef = useRef<HTMLDivElement>(null);
  const hasBootstrappedPromptRef = useRef(false);
  const promptParam = searchParams.get("prompt") ?? "";
  const activeConversationId =
    conversationId && conversationId !== "new" ? conversationId : undefined;
  const activeConversationIdRef = useRef(activeConversationId);
  activeConversationIdRef.current = activeConversationId;
  const activeConversation = activeConversationId
    ? conversations[activeConversationId]
    : undefined;
  const activeMessages = activeConversationId
    ? (messagesByConversationId[activeConversationId] ?? [])
    : [];
  const isAwaitingAiResponse = Boolean(
    activeConversationId && loadingByConversationId[activeConversationId],
  );
  const isLoadingMessages = Boolean(
    activeConversationId &&
    !(activeConversationId in messagesByConversationId) &&
    activeConversation,
  );

  const visibleMessages = activeMessages;

  const initConversations = useCallback(async () => {
    if (hasLoadedConversations) {
      setIsLoadingConversations(false);
      return;
    }

    setIsLoadingConversations(true);
    await dispatch(listGlobalChatConversationsAction());
    setIsLoadingConversations(false);
  }, [dispatch, hasLoadedConversations]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      const isCreatingConversation =
        !activeConversationId || !activeConversation;
      if (!isCreatingConversation) {
        await dispatch(
          sendGlobalChatMessageAction(activeConversation.id, trimmed),
        );
        return;
      }

      const localConversation = dispatch(
        createLocalGlobalChatConversationAction(user?.id ?? ""),
      );
      const optimisticMessageId = dispatch(
        addOptimisticGlobalChatMessageAction(
          localConversation.id,
          trimmed,
          user?.id ?? "",
        ),
      );
      activeConversationIdRef.current = localConversation.id;
      navigate(getChatConversationPath(localConversation.id));

      const conversation = await dispatch(
        persistGlobalChatConversationAction(localConversation.id),
      );
      if (activeConversationIdRef.current === localConversation.id) {
        activeConversationIdRef.current = conversation.id;
        navigate(getChatConversationPath(conversation.id), { replace: true });
      }

      const messagePromise = dispatch(
        sendGlobalChatMessageAction(
          conversation.id,
          trimmed,
          optimisticMessageId,
        ),
      );
      const titlePromise = dispatch(
        generateGlobalChatConversationTitleAction(conversation.id, trimmed),
      );
      await messagePromise;
      await titlePromise;
    },
    [activeConversation, activeConversationId, dispatch, navigate, user?.id],
  );

  useEffect(() => {
    initConversations();
  }, [initConversations]);

  useEffect(() => {
    if (!user || hasLoadedMemories) return;
    dispatch(listMemoriesAction(user.id));
  }, [dispatch, hasLoadedMemories, user]);

  useEffect(() => {
    if (!activeConversationId) return;
    dispatch(listGlobalChatMessagesAction(activeConversationId));
  }, [activeConversationId, dispatch]);

  useEffect(() => {
    const prompt = promptParam.trim();
    if (!prompt || hasBootstrappedPromptRef.current) return;

    hasBootstrappedPromptRef.current = true;
    sendMessage(prompt);
  }, [promptParam, sendMessage]);

  useEffect(() => {
    messagesRef.current?.scrollTo({
      top: messagesRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [visibleMessages.length, isAwaitingAiResponse, activeConversationId]);

  const conversationGroups = useMemo(
    () => groupConversationIds(conversationOrder, conversations),
    [conversationOrder, conversations],
  );
  const recommendedPrompts = useMemo(
    () =>
      getRecommendedQuestionPrompts(Object.values(memories), [
        "What happened last week?",
        "What did I discuss recently?",
        "What should I follow up on?",
      ]),
    [memories],
  );

  const shouldShowLoadingMessages =
    isLoadingMessages && visibleMessages.length === 0;
  const showEmptyChat =
    !shouldShowLoadingMessages &&
    visibleMessages.length === 0 &&
    !isAwaitingAiResponse;

  return (
    <main className={styles.chatPage}>
      <ChatSidebar
        activeConversationId={activeConversationId}
        conversationGroups={conversationGroups}
        conversations={conversations}
        isLoading={isLoadingConversations}
        isMobileOpen={isMobileSidebarOpen}
        loadingByConversationId={loadingByConversationId}
        onClose={() => setIsMobileSidebarOpen(false)}
      />
      <button
        type="button"
        className={classNames(styles.mobileSidebarBackdrop, {
          [styles.mobileSidebarBackdropOpen]: isMobileSidebarOpen,
        })}
        aria-label="Close chat history"
        onClick={() => setIsMobileSidebarOpen(false)}
      />

      <section className={styles.chatWorkspace}>
        <header className={styles.chatHeader}>
          <button
            type="button"
            className={styles.mobileHistoryButton}
            aria-label="Open chat history"
            aria-expanded={isMobileSidebarOpen}
            onClick={() => setIsMobileSidebarOpen(true)}
          >
            <Menu aria-hidden="true" size={20} strokeWidth={2} />
          </button>
          <div>
            <h2>
              {activeConversation?.title === "New chat" ? (
                <LoaderCircle
                  className={styles.titleSpinner}
                  aria-label="Generating chat title"
                  role="status"
                  size={24}
                  strokeWidth={2}
                />
              ) : (
                (activeConversation?.title ?? "New chat")
              )}
            </h2>
          </div>
        </header>

        <div className={styles.messagesPanel} ref={messagesRef}>
          {shouldShowLoadingMessages ? (
            <div className={styles.messageSkeleton} aria-label="Loading chat">
              <span />
              <span />
              <span />
            </div>
          ) : showEmptyChat ? (
            <EmptyChat
              disabled={isAwaitingAiResponse}
              onPrompt={sendMessage}
              prompts={recommendedPrompts}
            />
          ) : (
            visibleMessages.map((message) => (
              <ChatMessageBubble
                key={message.id}
                memories={memories}
                message={message}
              />
            ))
          )}
          {isAwaitingAiResponse && <ThinkingIndicator />}
        </div>

        <div className={styles.composerDock}>
          <ChatComposer
            disabled={isAwaitingAiResponse || isLoadingMessages}
            onSend={sendMessage}
            placeholder="Ask anything across your memories..."
          />
        </div>
      </section>
    </main>
  );
};
